<template>
	<v-card dark outlined class="stats-current-rate-card pa-3" style="background-color:#1E1E1E; border-color:#2D2D2D;">
		<div class="d-flex align-center justify-space-between mb-1">
			<div class="caption grey--text text-uppercase" style="letter-spacing:0.05em;">
				<v-icon small left color="#FFA726">mdi-speedometer</v-icon>{{ $ml.get('alarm_stats_current_rate') }}
			</div>
		</div>

		<!-- Gauge canvas container -->
		<div class="gauge-wrapper">
			<canvas ref="gaugeCanvas" height="140"></canvas>
		</div>

		<!-- Stats row below gauge -->
		<v-row no-gutters class="mt-1 text-center">
			<v-col cols="4">
				<div class="caption grey--text">{{ $ml.get('alarm_stats_per_hour') }}</div>
				<div class="subtitle-2 font-weight-bold" style="color:#FFA726;">
					{{ perHour }}
				</div>
			</v-col>
			<v-col cols="4">
				<div class="caption grey--text">{{ $ml.get('alarm_stats_peak_today') }}</div>
				<div class="subtitle-2 font-weight-bold" style="color:#FFB74D;">
					{{ formatNumber(peakRate) }}
				</div>
			</v-col>
			<v-col cols="4">
				<div class="caption grey--text">{{ $ml.get('alarm_stats_avg_hour') }}</div>
				<div class="subtitle-2 font-weight-bold" style="color:#FFA726;">
					{{ avgPerHour }}
				</div>
			</v-col>
		</v-row>
	</v-card>
</template>

<script>
import Chart from 'chart.js';

export default {
	name : 'StatsCurrentRate',

	props : {
		currentRate : {
			type    : Number,
			default : 0,
		},
		targetRate : {
			type    : Number,
			default : 0,
		},
		peakRate : {
			type    : Number,
			default : 0,
		},
		avgRate : {
			type    : Number,
			default : 0,
		},
	},

	data() {
		return {
			chart : null,
		};
	},

	computed : {
		// current units/min rounded
		displayRate() {
			return Math.round(this.currentRate || 0);
		},

		// scale per hour
		perHour() {
			return this.formatNumber(Math.round((this.currentRate || 0) * 60));
		},

		avgPerHour() {
			return this.formatNumber(Math.round((this.avgRate || 0) * 60));
		},

		// arc fill percentage — cap at 1 so gauge never overflows
		fillFraction() {
			const target = this.targetRate || 1;
			return Math.min(1, (this.currentRate || 0) / target);
		},

		// dynamic arc color based on fill fraction
		arcColor() {
			if (this.fillFraction >= 0.85) return '#66BB6A'; // green — on target
			if (this.fillFraction >= 0.5)  return '#FFA726'; // orange — moderate
			return '#EF5350';                                  // red — low
		},
	},

	watch : {
		currentRate() {
			this.updateChart();
		},
		targetRate() {
			this.updateChart();
		},
	},

	mounted() {
		this.$nextTick(() => {
			this.createChart();
		});
	},

	beforeDestroy() {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	},

	methods : {
		formatNumber(value) {
			if (value === null || value === undefined) return '0';
			return Number(value).toLocaleString();
		},

		createChart() {
			if (!this.$refs.gaugeCanvas) return;

			const ctx       = this.$refs.gaugeCanvas.getContext('2d');
			const fillValue = this.fillFraction;
			const remaining = 1 - fillValue;
			const self      = this;

			// Register the center-text plugin inline (Chart.js 2 style)
			const centerTextPlugin = {
				beforeDraw(chart) {
					const width  = chart.chart.width;
					const height = chart.chart.height;
					const ctx2   = chart.chart.ctx;

					ctx2.restore();

					// Main rate number
					const fontSize = (height / 80).toFixed(2);
					ctx2.font      = `bold ${fontSize}em sans-serif`;
					ctx2.fillStyle = self.arcColor;
					ctx2.textBaseline = 'middle';

					const rateText = String(self.displayRate);
					const textX    = Math.round((width - ctx2.measureText(rateText).width) / 2);
					const textY    = height * 0.58;
					ctx2.fillText(rateText, textX, textY);

					// Sub-label "units/min"
					const subFontSize = (height / 130).toFixed(2);
					ctx2.font      = `${subFontSize}em sans-serif`;
					ctx2.fillStyle = '#9E9E9E';
					const subText  = self.$ml.get('alarm_stats_upm');
					const subX     = Math.round((width - ctx2.measureText(subText).width) / 2);
					ctx2.fillText(subText, subX, textY + 18);

					ctx2.save();
				},
			};

			this.chart = new Chart(ctx, {
				type   : 'doughnut',
				plugins : [centerTextPlugin],
				data   : {
					datasets : [
						{
							data            : [fillValue, remaining],
							backgroundColor : [this.arcColor, '#2D2D2D'],
							borderWidth     : 0,
							hoverBackgroundColor : [this.arcColor, '#2D2D2D'],
						},
					],
				},
				options : {
					rotation      : -Math.PI,
					circumference : Math.PI,
					cutoutPercentage : 75,
					responsive    : true,
					maintainAspectRatio : true,
					animation     : {
						animateRotate : true,
						duration      : 600,
					},
					legend   : { display: false },
					tooltips : { enabled: false },
				},
			});
		},

		updateChart() {
			if (!this.chart) return;

			const fillValue = this.fillFraction;
			const remaining = 1 - fillValue;

			this.chart.data.datasets[0].data            = [fillValue, remaining];
			this.chart.data.datasets[0].backgroundColor = [this.arcColor, '#2D2D2D'];
			this.chart.data.datasets[0].hoverBackgroundColor = [this.arcColor, '#2D2D2D'];
			this.chart.update();
		},
	},
};
</script>

<style scoped>
.stats-current-rate-card {
	min-height: 200px;
}

.gauge-wrapper {
	position: relative;
	width: 100%;
	max-width: 220px;
	margin: 0 auto;
}
</style>
